/**
 * Temporary auth debug strip. Used during scaffolding to visually verify
 * the api client + /api/me round-trip. Removed once real login form lands.
 */
import { useAuth, useLogout } from "@/hooks/use-auth";
import { roleDisplayName } from "@/lib/api-types";
import { cn } from "@/lib/utils";

export function AuthDebug({ className }: { className?: string }) {
  const { user, role, workspaceName, isLoading, isFetching, isError, error } = useAuth();
  const logout = useLogout();

  const status: {
    label: string;
    tone: "muted" | "brand" | "destructive";
    detail?: string;
  } = (() => {
    if (isLoading) return { label: "Checking session…", tone: "muted" };
    if (isError)
      return {
        label: "API unreachable",
        tone: "destructive",
        detail: (error as Error)?.message,
      };
    if (!user) return { label: "Not signed in", tone: "muted" };
    return {
      label: `Signed in as ${user.email}`,
      tone: "brand",
      detail: [roleDisplayName(role), workspaceName].filter(Boolean).join(" · "),
    };
  })();

  const tone = {
    muted: "border-border bg-card/60 text-muted-foreground",
    brand: "border-brand/30 bg-brand-soft text-foreground",
    destructive: "border-destructive/40 bg-destructive/5 text-destructive",
  }[status.tone];

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-3 rounded-lg border px-4 py-2.5 text-xs backdrop-blur",
        tone,
        className
      )}
    >
      <span className="inline-flex items-center gap-2 font-medium">
        <span
          className={cn(
            "h-1.5 w-1.5 rounded-full",
            status.tone === "brand" && "bg-brand",
            status.tone === "muted" && "bg-muted-foreground/50",
            status.tone === "destructive" && "bg-destructive"
          )}
        />
        {status.label}
        {isFetching && !isLoading ? (
          <span className="text-muted-foreground">· refreshing</span>
        ) : null}
      </span>
      {status.detail && (
        <span className="text-muted-foreground">{status.detail}</span>
      )}
      {user && (
        <button
          type="button"
          onClick={() => logout.mutate()}
          disabled={logout.isPending}
          className="ml-auto rounded-md border border-border bg-card px-2 py-1 font-medium transition-colors hover:bg-secondary disabled:opacity-50"
        >
          {logout.isPending ? "Signing out…" : "Sign out"}
        </button>
      )}
    </div>
  );
}
